<template>
    <div class="guide-manager">
        <div v-if="getChannelNumber" class="guide-manager__input-number"> {{ getChannelNumber }} </div>
    </div>
</template>

<script setup lang="ts">
    import { type Channel } from "@ca.vmedia.tv/domains";
    import { useApplicationStore } from "@/stores/Application";
    import { Strings } from "@/locales/types/enums/strings";
    
    const ApplicationStore = useApplicationStore();
    const {
        setTooltipeShowing,
        setTooltipeMessage,
    } = ApplicationStore;

    const props = defineProps({
        channels: {
            type: [Array, null] as PropType<Channel[] | null>,
            default: () => [],
        },
    });

    const { $navigator } = useNuxtApp();
    const emit = defineEmits(['founded', 'not-founded']);
    const { t } = useI18n();

    const timerId: Ref<ReturnType<typeof setTimeout> | null> = ref(null);
    const clearTimerId = () => {
        if (timerId.value) {
            clearTimeout(timerId.value);
            timerId.value = null;
        }
    };
    const updateChannelsList = (accessNum: string) => {
        if (timerId.value) {
            clearTimerId();
        }
        timerId.value = setTimeout(() => {
            const channel = getChannelByAccessNumber(accessNum);
            if (channel?.id) {
                emit('founded', channel.id);
            } else {
                showTooltipeGuideManager(accessNum);
            }
            _channelNumber.value = '';
        }, 3000);
    };
   
    const showTooltipeGuideManager = (accessNum: string) => {
        const text: string = t(Strings.GUIDE_MANAGER_TOOLTIP, {accessNum: accessNum}).toString();
        setTooltipeMessage(text);
        setTooltipeShowing({visible: true});
    };

    const _channelNumber: Ref<string> = ref('');
    const setChannelNumber = (number: string): void => {
        _channelNumber.value = _channelNumber.value + number;
    };
    const getChannelNumber = computed((): string => {
        return _channelNumber.value;
    });
    const getChannelByAccessNumber = (accessNum: string): Channel | undefined => {
        return props.channels?.find(channel => channel.accessNum === Number(accessNum));
    };

    const isRegKeyListeners: Ref<boolean> = ref(false);
    const registerKeyListeners = () => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:number", keyNumberListener);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = () => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:number", keyNumberListener);
            isRegKeyListeners.value = false;
        }
    };
    const keyNumberListener = (event: KeyboardEvent) => {
        setTooltipeShowing({visible: false});
        setChannelNumber(event.key);
    };

    onMounted(() => {
        registerKeyListeners();
    });

    onBeforeUnmount(() => {
        unregisterKeyListeners();
        clearTimerId();
    });

    watch(
        getChannelNumber,
        (value: string) => {
            if(value === '') {
                return;
            }
            updateChannelsList(value);
        }
    );
</script>

<style lang="scss">
    @import url("styles.scss");
</style>